import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import EditNoteIcon from '@mui/icons-material/EditNote';
import HistoryIcon from '@mui/icons-material/History';
import InfoOutlineIcon from '@mui/icons-material/InfoOutlined';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Box,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { SingleOperators } from 'common/src/query-params/operators';
import { GENERATED_ROUTES } from 'common/src/routes/GeneratedRoutes';
import { BillEntryMethod } from 'common/src/types/enums/BillEntryMethod.enum';
import { InfoOnlyReason } from 'common/src/types/enums/InfoOnlyReason.enum';

import { dismissAllocationReview } from 'portal/src/api/BillsService';
import { QUERY_KEYS } from 'portal/src/api/constants';
import ConfirmDialog from 'portal/src/components/common/ConfirmDialog';
import useSnackbarHandler from 'portal/src/utils/Snackbar/useSnackbarHandler';

const determineFormTitle = (
  entryMethod: BillEntryMethod,
): { title: string; tooltip: string } => {
  switch (entryMethod) {
    case BillEntryMethod.Allocated:
      return {
        title: 'Allocated Bill Information',
        tooltip: 'This bill was allocated according to an allocation template.',
      };
    case BillEntryMethod.Automatic:
      return {
        title: 'Automated Bill Information',
        tooltip:
          "This bill was created through the platform's bill automation process.",
      };
    case BillEntryMethod.Manual:
      return {
        title: 'Bill Information',
        tooltip: 'This bill was entered manually through the platform.',
      };
    case BillEntryMethod.Imported:
      return {
        title: 'Imported Bill Information',
        tooltip: 'This bill was imported in a bulk upload.',
      };
    case BillEntryMethod.Transferred:
      return {
        title: 'Transferred Bill Information',
        tooltip:
          'This bill was ingested through another software system and ported over to the platform.',
      };
    default:
      return { title: 'Bill Information', tooltip: '' };
  }
};

const buttonStyles = {
  fontSize: 17,
  fontWeight: 'medium',
};

// Open in a new tab since the bills table only reads filters from the url when it is first loaded
const newTabLinkProps = {
  clickable: true,
  component: 'a',
  rel: 'noopener noreferrer',
  target: '_blank',
} as const;

const getCorrectedBillsUrl = (correctionBillId: string) => {
  const filters = [
    {
      field: 'correctedBy',
      operator: SingleOperators.EQUALS,
      value: correctionBillId,
    },
  ];

  return `${GENERATED_ROUTES.ADMINISTRATION.BILLS}?filters=${encodeURIComponent(JSON.stringify(filters))}`;
};

const BillFormHeader = ({
  billId,
  billEntryMethod,
  correctedBy,
  infoOnly,
  infoOnlyReason,
  isCorrection,
  isPaid,
  showWarningBadge,
}: {
  billId?: string;
  billEntryMethod?: BillEntryMethod;
  correctedBy?: string;
  infoOnly?: boolean;
  infoOnlyReason?: InfoOnlyReason;
  isCorrection?: boolean;
  isPaid?: boolean;
  showWarningBadge?: boolean;
}) => {
  const snackbarHandler = useSnackbarHandler();
  const client = useQueryClient();
  const { title, tooltip } = determineFormTitle(billEntryMethod);

  const [showDismissDialog, setShowDismissDialog] = useState(false);

  const { mutate: dismissAllocationWarning, isPending } = useMutation({
    mutationFn: async () =>
      dismissAllocationReview(billId, { allocationsEdited: true }),
    onSuccess: () => {
      setShowDismissDialog(false);
      client.invalidateQueries({ queryKey: [QUERY_KEYS.detailPageData] });
    },
    onError: () => snackbarHandler.error('Unable to dismiss warning.'),
  });

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        width: '100%',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="h6">{title}</Typography>
        {tooltip && (
          <Tooltip title={tooltip}>
            <InfoOutlineIcon fontSize="small" />
          </Tooltip>
        )}
      </Box>
      <Stack direction="column" spacing={1} sx={{ alignItems: 'flex-end' }}>
        {isPaid && (
          <Chip
            color="success"
            icon={<CheckCircleOutlineOutlinedIcon />}
            label="PAID"
            variant="outlined"
            size="small"
            sx={buttonStyles}
          />
        )}
        {isCorrection && billId && (
          <Tooltip title="View the bills corrected by this bill">
            <Chip
              {...newTabLinkProps}
              color="info"
              href={getCorrectedBillsUrl(billId)}
              icon={<EditNoteIcon />}
              label="CORRECTION"
              size="small"
              sx={buttonStyles}
              variant="outlined"
            />
          </Tooltip>
        )}
        {correctedBy && (
          <Tooltip title="View the bill that corrected this bill">
            <Chip
              {...newTabLinkProps}
              color="secondary"
              href={`${GENERATED_ROUTES.ADMINISTRATION.BILLS}/${correctedBy}`}
              icon={<HistoryIcon />}
              label="HAS BEEN CORRECTED"
              size="small"
              sx={buttonStyles}
              variant="outlined"
            />
          </Tooltip>
        )}
        {infoOnly && (
          <Chip
            color="warning"
            icon={<InfoOutlineIcon />}
            label={['INFO ONLY', infoOnlyReason?.toUpperCase()]
              .filter(Boolean)
              .join(': ')}
            size="small"
            sx={buttonStyles}
            variant="outlined"
          />
        )}
        {showWarningBadge && (
          <Chip
            color="warning"
            icon={<InfoOutlineIcon />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body1" sx={{ ...buttonStyles }}>
                  Review Allocation Line Items
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setShowDismissDialog(true)}
                  sx={{ padding: 0 }}
                >
                  <CancelIcon fontSize="small" color="warning" />
                </IconButton>
              </Box>
            }
            size="small"
            variant="outlined"
          />
        )}
      </Stack>
      <ConfirmDialog
        open={showDismissDialog}
        onClose={() => setShowDismissDialog(false)}
        message={
          <Typography>
            This bill was allocated using estimated monthly percentages.
            <br />
            By dismissing this warning, you are confirming the bill's line items
            are accurate.
          </Typography>
        }
        primaryButtonLabel="Yes, Dismiss"
        onPrimaryButtonClicked={dismissAllocationWarning}
        secondaryButtonLabel="Cancel"
        onSecondaryButtonClicked={() => setShowDismissDialog(false)}
        renderElement={<WarningIcon sx={{ color: 'warning.main' }} />}
        isLoading={isPending}
      />
    </Box>
  );
};

export default BillFormHeader;
